import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import { FadeIn } from '@/components/marketing/FadeIn';

interface Step {
  num: number;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    num: 1,
    title: 'Connect your platforms',
    body: "Link Reddit, Bluesky, YouTube, and X in seconds via secure OAuth. Your passwords never touch our servers — we store only the tokens required to fetch your content. Once connected, SocialScoop keeps pulling in the background. Your feed updates whether you're in the app or not.",
  },
  {
    num: 2,
    title: 'Set your standards',
    body: "A short onboarding quiz asks about the topics you want more of and the content that drains you. Your answers build your first set of Listening Rules and Fences automatically — plain English, no Boolean logic, nothing to configure manually. You own every rule from the start. Edit, add, or remove them anytime.",
  },
  {
    num: 3,
    title: 'We do the work. You get the result.',
    body: 'Every 20 minutes, SocialScoop fetches new posts across all your platforms, scores each one against your rules, runs Claude AI sentiment and toxicity analysis, and applies your Intensity Gauge settings. By the time you open the app, everything is already curated. You read. You don\'t scroll and hope.',
  },
  {
    num: 4,
    title: 'Open your Curated Feed',
    body: "The Curated tab shows only what passed your standards — sorted by published date, newest first, exactly as you'd expect a real feed to work. Strong Listening Rule matches surface to the top. Fenced content moves quietly to the Vault, not deleted — just out of the way until you want it.",
  },
  {
    num: 5,
    title: 'Tune with the Intensity Gauge',
    body: "Every rule has an Intensity Gauge — a 0–5 slider. Listening Rules: 0 = off, 5 = always prioritize. Fences: 0 = off, 5 = never show. You're not flipping switches. You're tuning. Most people find their feed feeling right within the first week of minor adjustments.",
  },
  {
    num: 6,
    title: 'Nothing is hidden. Everything is yours.',
    body: "Content that matches your Fences goes to the Content Vault — PIN-protected, fully reviewable, recoverable anytime. We don't moderate your feed. We execute your preferences. You set the standards. You can always change them. This is what Feed Sovereignty actually looks like.",
  },
];

const FAQS = [
  {
    q: 'How often does my feed update?',
    a: "Every 20 minutes, automatically. You don't need to be in the app.",
  },
  {
    q: 'Which AI model powers the scoring?',
    a: 'Claude, by Anthropic. OpenAI and Google Gemini support coming soon. You choose your preferred model in Settings.',
  },
  {
    q: 'What actually happens to fenced content?',
    a: "It moves to the Content Vault — PIN-protected and reviewable whenever you want. Nothing is deleted. Nothing is hidden permanently. You can recover any post, adjust any fence, or turn the whole system off. It's your feed.",
  },
  {
    q: 'Can I add my own articles, newsletters, or RSS feeds?',
    a: "Yes. Paste any RSS or Substack URL into a Listening Rule and it'll pull into your curated feed automatically. Academics and researchers use this to mix their own publications into their public curator feeds.",
  },
  {
    q: 'Is my data sold or used to train models?',
    a: 'No. Never. Our business model is subscriptions. Your reading behavior, your rules, and your preferences belong to you.',
  },
  {
    q: 'Can I share my curated feed with others?',
    a: "Yes — make your feed public from Settings and share your profile link. Followers can browse your curated stream. Founding Members can monetize their audience through our curator program.",
  },
];

const HowItWorks: React.FC = () => {
  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      <Header />

      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <FadeIn>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">How It Works</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1]">
            A feed that works before you open it.
          </h1>
        </FadeIn>
        <FadeIn delay={100}>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Here's exactly what happens — from signup to a feed that's finally yours.
          </p>
        </FadeIn>
      </section>

      <section className="border-t border-border/60">
        <div className="max-w-3xl mx-auto px-6 py-20 space-y-14">
          {STEPS.map((step) => (
            <FadeIn key={step.num}>
              <div className="grid grid-cols-[auto_1fr] gap-6 items-start">
                <div className="text-xs uppercase tracking-wider text-muted-foreground tabular-nums pt-1">
                  Step {step.num}
                </div>
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">{step.title}</h2>
                  <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-border/60 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6 py-20 space-y-8">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Questions worth answering</h2>
          {FAQS.map((item) => (
            <div key={item.q}>
              <h3 className="font-medium">{item.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border/60">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
            You've read enough. Try it.
          </h2>
          <Button asChild size="lg">
            <Link to="/auth">Start for free →</Link>
          </Button>
          <p className="mt-4 text-xs text-muted-foreground">
            Start for $1. Scales with what you use. No credit card required to try.
          </p>
        </div>
      </section>

      <footer className="border-t border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© 2026 SocialScoop.ai — Built for people, not platforms.</div>
          <nav className="flex flex-wrap gap-5">
            <Link to="/features" className="hover:text-foreground transition-colors">Features</Link>
            <Link to="/how-it-works" className="hover:text-foreground transition-colors">How It Works</Link>
            <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link to="/data-deletion" className="hover:text-foreground transition-colors">Data Deletion</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;
